import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * StackBlockView
 *
 * Card representation of a Stack (a Collection with `layerIndex = 2`)
 * inside a parent Collection's grid. The peek tiles are scattered
 * (not fanned) — each one is positioned with a deterministic-but-
 * jittered transform derived from a hash of the stack id + slot
 * index, giving every Stack a unique silhouette while staying stable
 * across re-renders.
 *
 * Bottom-anchored full-width pill carries the title + member count.
 * Its background, border, and text are all per-stack tinted via
 * `getCollectionPaletteColor` so the pill reads as the same surface
 * as the StackFrame outline that wraps the open unfurl.
 *
 * Clicking the *tile* unfurls the Stack inline inside the surrounding
 * `<Stream>` — the tile click is intentionally NOT a navigation, since
 * Stacks are sub-organization within a Collection. The Stream wires
 * `onOpen` to its `useStackUnfurl().toggleStack` and passes `isOpen`
 * so the tile can fade the scatter + pill out and a circular close
 * button (top-left X) in while the unfurl is showing. ESC also closes
 * the unfurl.
 *
 * Once the Stack is unfurled, clicking the *title text* inside the
 * pill IS a navigation — it routes to the collection page
 * (`/collection/:id`) of the Stack itself for real Stacks, or of the
 * synthetic stack's PARENT Collection on the homepage (synthetic
 * stacks have no server identity). A hover-revealed `→` arrow + a
 * `text-decoration: underline` on the title flag the affordance only
 * in the open state — when closed the title is rendered as a plain
 * `<span>` so the entire tile reads as a single "click to open"
 * affordance, with no surprise hidden actions on the title region.
 * The open-state button stops propagation so the tile's close
 * handler doesn't run on the same click.
 */
import React, { memo, useCallback, useLayoutEffect, useMemo, useRef, useState, } from "react";
import { useNavigate } from "react-router-dom";
import { useDndMonitor, useDroppable } from "@dnd-kit/core";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { faArrowRight, faCheck, faCircleNotch, faPencil, faThumbtack, faTrashCan, faXmark, } from "@fortawesome/pro-solid-svg-icons";
import { FaIcon } from "@shared/client/components/fa-icon";
import { showToast } from "@shared/client/components/toast/show-toast";
import { Tooltip } from "@shared/client/components/ui/tooltip/tooltip";
import { memoForwardRef } from "@shared/client/util/memo";
import { listCollectionEffectiveMembers } from "@/api/query-api";
import { collectionEffectiveMembersQueryKey } from "@/api/collection-members-query-key";
import { BlockMiniCapture } from "@/components/block-mini-capture/block-mini-capture";
import { useCollectionCardPreviews } from "@/components/collection-block-view/use-collection-card-previews";
import { getCollectionCardColors, getCollectionPaletteColor } from "@/config/block-styles";
import { collectionStore } from "@/data/collection-store";
import { useApp } from "@/state/app-context";
import { useAccountIdentity } from "@/hooks/use-account-identity";
import { useLongPressStackSelect } from "@/hooks/use-long-press-stack-select";
import { usePrimeAccountAttributionFromMembers } from "@/hooks/use-prime-account-attribution-from-members";
import { MAX_PINS_PER_SURFACE } from "@shared/types/pin";
import { togglePinWithSync, usePinList } from "@/state/pin-store";
import { formatRelativeTime } from "@/lib/format-relative-time";
import { track } from "@/lib/analytics";
import { hashId } from "@/lib/entry-context";
import { useUrlForCollectionId } from "@/lib/urls";
import "./stack-block-view.scss";
// Mirrors `CreateCollectionSchema.title` server-side. The server will
// reject longer titles outright; clamping in the input gives the user
// immediate feedback instead of a delayed toast.
const MAX_TITLE_LENGTH = 200;
// Tunables for the scatter — tight enough that every tile reads as
// "in" the card, loose enough that no two stacks look the same.
//
// The peek tiles are now landscape (close to a real block's ~230x173
// footprint), so the rotation channel sweeps a much wider arc than it
// did with the old 88x88 squares. Pulling translate + rotation down
// keeps the rotated rectangle's corner extension within the
// surrounding card's bounds (~card-width/2 from center) — without
// this, the peeks would visibly poke past adjacent grid cards.
const MAX_TRANSLATE = 8;
const MAX_ROTATE = 5;
/**
 * Cheap deterministic hash. Same input → same output across reloads,
 * which is what gives a Stack its persistent silhouette. Borrows the
 * 32-bit FNV-1a constants — collisions are fine here, we only need
 * spread.
 */
function hashString(input) {
    let h = 0x811c9dc5;
    for (let i = 0; i < input.length; i++) {
        h ^= input.charCodeAt(i);
        h = Math.imul(h, 0x01000193);
    }
    return h >>> 0;
}
/**
 * Map a 32-bit hash to a value in `[-1, 1]` by splitting the integer
 * into 16-bit halves and folding them with a small offset for the
 * second draw. Returns deterministic, decoupled offsets so the X / Y /
 * rotation channels don't visibly correlate.
 */
function hashToUnit(hash, channel) {
    const shifted = (hash >>> (channel * 5)) & 0xffff;
    return (shifted / 0xffff) * 2 - 1;
}
function jitterFor(stackId, slotIndex, channel) {
    const hash = hashString(`${stackId}:${slotIndex}:${channel}`);
    return hashToUnit(hash, channel);
}
// ---------------------------------------------------------------------------
// SyntheticAddedByAvatars
//
// Horizontal overlapping row of the people who authored the grouped
// blocks. Rendered to the left of the "N blocks · ..." meta in the
// synthetic-stack pill. Mirrors the existing `useAccountIdentity`
// contract (warm-tan circle with an initial fallback when no portrait
// is available) so the chrome reads consistently with the lip avatar
// and the comment-popover avatar.
//
// Distinct from `AnnotationAuthorAvatars`:
//   - row-flexed (horizontal), not column-stacked
//   - subjects are block authors, not annotation contributors
//   - feeds off an already-deduped `accountIds: string[]` so no
//     subscription work happens here
//
// Capped at MAX_AVATARS with a `+N` overflow chip; anything beyond
// would crowd the pill's first line for a feed with many contributors.
// ---------------------------------------------------------------------------
const SYNTHETIC_AVATAR_MAX = 4;
const SyntheticAddedByAvatars = memo(function SyntheticAddedByAvatars({ accountIds, }) {
    if (accountIds.length === 0)
        return null;
    const visible = accountIds.slice(0, SYNTHETIC_AVATAR_MAX);
    const overflow = accountIds.length - visible.length;
    return (_jsxs("div", { className: "stack-block-view__added-by", "aria-hidden": true, children: [visible.map((accountId) => (_jsx(SyntheticAddedByAvatar, { accountId: accountId }, accountId))), overflow > 0 ? (_jsxs("span", { className: "stack-block-view__added-by-overflow", children: ["+", overflow] })) : undefined] }));
});
const SyntheticAddedByAvatar = memo(function SyntheticAddedByAvatar({ accountId, }) {
    const identity = useAccountIdentity(accountId);
    return (_jsx("span", { className: "stack-block-view__added-by-avatar", children: identity.avatarSrc !== undefined ? (_jsx("img", { src: identity.avatarSrc, alt: "", className: "stack-block-view__added-by-avatar-image" })) : (_jsx("span", { className: "stack-block-view__added-by-avatar-initial", children: identity.initial })) }));
});
function placePeekTiles(stackId, envelopes) {
    return envelopes.map((envelope, index) => {
        const jitterX = jitterFor(stackId, index, 0) * MAX_TRANSLATE;
        const jitterY = jitterFor(stackId, index, 1) * MAX_TRANSLATE;
        const jitterR = jitterFor(stackId, index, 2) * MAX_ROTATE;
        // Bias z-index by hash so the front-most tile is stable per stack.
        // Array index alone would always put the first envelope on top,
        // which makes every Stack feel identical at a glance.
        const zHash = hashString(`${stackId}:${index}:z`);
        const z = (zHash & 0xff) + (envelopes.length - index) * 2;
        return { envelope, jitterX, jitterY, jitterR, z };
    });
}
export const StackBlockView = memoForwardRef(({ stack, isOpen, onOpen, synthetic, darkBg }, ref) => {
    const localRef = useRef(null);
    const app = useApp();
    const navigate = useNavigate();
    const buildUrlForCollectionId = useUrlForCollectionId();
    const isSynthetic = synthetic !== undefined;
    // When `synthetic.useRealStackPill` is set, the tile uses the
    // synthetic-envelope rendering path (FLIP id, preview envelopes,
    // no server-children fetch) but presents with the REAL-stack pill
    // layout (title on top, count + last-added below). Powers the
    // homepage Stream's user-created Stack-under-Primary tiles where
    // the underlying parent IS a real Stack — the synthetic mechanism
    // just gives us instant inline rendering without a round-trip.
    const useSyntheticPillLayout = isSynthetic === true && synthetic.useRealStackPill !== true;
    // "Effective" stack id used by every chrome path that mutates the
    // underlying server-side Stack (rename, soft-delete, multiselect
    // toggle/highlight). Three cases:
    //   - Real Stack tile (non-synthetic): `stack.id` — the row id.
    //   - Synthetic Stack-under-Primary: `synthetic.realStackId` —
    //     the synthetic mechanism is just the inline-rendering path,
    //     so mutations route to the real Stack's server row. Without
    //     this, the homepage tile would lose pencil + trash + long-
    //     press multiselect that the same Stack offers when seen via
    //     its parent Collection / Group view, producing a visible
    //     divergence between two surfaces that render the same row.
    //   - Pure synthetic (same-collection-per-day): `undefined` — the
    //     tile has no server identity at all, so every server-
    //     mutating affordance stays suppressed.
    // A single `undefined`/non-`undefined` gate (`hasRealStackChrome`)
    // drives all the affordances below so the three branches stay in
    // lockstep — fixing one without the others is what produced the
    // earlier mixed state.
    const effectiveStackId = isSynthetic === false ? stack.id : synthetic.realStackId;
    const hasRealStackChrome = effectiveStackId !== undefined;
    // Navigation target for the title-click affordance:
    //   - real Stack: navigate to the Stack's own collection page
    //     (it's a layer-2 collection with a real id + slug).
    //   - synthetic Stack: navigate to its REAL parent Collection
    //     (synthetic ids have no server identity, so the synthetic
    //     itself doesn't resolve). `paletteId` is already the parent
    //     id by construction — same value drives the per-stack hue,
    //     so reusing it keeps the source of truth in one place.
    const navigationCollectionId = isSynthetic ? synthetic.paletteId : stack.id;
    // Real stacks fetch their peek previews from the per-collection
    // block index. Synthetic stacks already know the exact blocks they
    // represent (the day's grouped set) — pass `enabled: false` so the
    // hook short-circuits its IntersectionObserver + loadBlocks effects
    // (the latter would 404 against a synthetic id that has no
    // server-side identity), and we sub in the override list ourselves.
    const previewQuery = useCollectionCardPreviews(stack, localRef, {
        enabled: isSynthetic === false,
    });
    const previewEnvelopes = isSynthetic ? synthetic.previewEnvelopes : previewQuery.envelopes;
    const count = isSynthetic ? synthetic.count : previewQuery.count;
    const paletteId = isSynthetic ? synthetic.paletteId : stack.id;
    // Prime `accountAttributionStore` for the synthetic-stack tile's
    // overlapping avatar row. Without this, the closed-state tile reads
    // from an empty store and falls back to placeholder initials —
    // avatars then "pop in" only after the user unfurls the stack
    // (because each inner `<BlockViewContainer>` runs the same prime
    // path on its own collection lookup). Priming the parent
    // collection here resolves the avatars on first paint of the
    // closed tile, matching the lip's contract on regular block cards.
    //
    // For synthetic stacks `paletteId === parent.id` by construction;
    // real Stacks skip the prime since they don't render avatars on
    // their tile and the per-block prime path already covers their
    // unfurled contents.
    const queryClient = useQueryClient();
    const syntheticPrimeCollectionId = isSynthetic ? paletteId : undefined;
    const membersQuery = useQuery({
        queryKey: collectionEffectiveMembersQueryKey(syntheticPrimeCollectionId ?? ""),
        queryFn: () => listCollectionEffectiveMembers(syntheticPrimeCollectionId ?? ""),
        enabled: syntheticPrimeCollectionId !== undefined,
        staleTime: 15_000,
    });
    usePrimeAccountAttributionFromMembers(membersQuery.data, queryClient, syntheticPrimeCollectionId, membersQuery.dataUpdatedAt, syntheticPrimeCollectionId !== undefined);
    // Parent Collection for "Pin to {parent}" affordance. A real Stack
    // always has a non-null `parentCollectionId` (enforced in the entity
    // layer); a synthetic stack intentionally has no parent because it
    // doesn't exist server-side — so the lookup short-circuits to an
    // ineligible state and the pin button stays hidden.
    const parent = collectionStore.useFindById(stack.parentCollectionId);
    const canPin = isSynthetic === false && parent !== undefined && parent.type !== "primary";
    const parentId = canPin ? parent.id : undefined;
    const parentTitle = canPin ? parent.title ?? "Untitled" : undefined;
    // Subscribe to the parent collection's pin list so the button can
    // reflect — and toggle — the live state. A Stack has exactly one
    // pin destination (the parent Collection's default surface), so a
    // dropdown of options would be pure friction; the click toggles
    // directly. The hook is always called (rules of hooks); the scope
    // falls back to a sentinel id when there's no parent so the
    // subscription is inert without breaking hook ordering.
    const pinScope = useMemo(() => ({ type: "collection", id: parentId ?? "__none__" }), [parentId]);
    const parentPins = usePinList(pinScope, "default");
    const isPinned = parentId !== undefined &&
        parentPins.some((p) => p.type === "collection" && p.id === stack.id);
    const handleTogglePin = useCallback((e) => {
        e.stopPropagation();
        if (parentId === undefined)
            return;
        void togglePinWithSync({ type: "collection", id: parentId }, "default", {
            type: "collection",
            id: stack.id,
        }).then((result) => {
            if (result.ok === false && result.reason === "max") {
                showToast.warning(`You cannot pin more than ${MAX_PINS_PER_SURFACE} items here`);
            }
        });
    }, [parentId, stack.id]);
    // Inline-rename state. The pencil affordance fades in on card hover
    // (see `.stack-block-view__rename-btn` in the .scss); clicking it
    // swaps the pill's title text for an input and seeds the draft with
    // the stack's current stored title (NOT the "Untitled stack"
    // placeholder, which would mislead the user into thinking that
    // literal string is what's saved). Enter commits, Escape cancels,
    // blur commits — mirroring the inline-rename convention used in
    // Notion / Linear / etc.
    //
    // Commit path is fire-and-forget against
    // `collectionStore.updateTitle`, which writes the new title to BOTH
    // the cross-layer cache and the parent-children tanstack-query
    // caches in the same React commit. The input unmounts immediately,
    // and the pill re-renders with the new prop value on the very next
    // tick — no spinner state needed here because the user sees the
    // result instantly. Errors fall back to a toast + automatic
    // rollback inside the store.
    const [isEditing, setIsEditing] = useState(false);
    const [draftTitle, setDraftTitle] = useState("");
    const [isDeleting, setIsDeleting] = useState(false);
    const inputRef = useRef(null);
    // Long-press → enter stack-multiselect mode (with this stack
    // seeded). No-op while any multiselect mode is already active —
    // the click handler below covers the toggle path. Pure synthetic
    // groupings (no `effectiveStackId`) skip this: the rest of the
    // app's multiselect-on-stacks plumbing would treat the synthetic
    // id as a real stack and write it into the selection set, which
    // doesn't round-trip cleanly anywhere. Synthetic-as-real-stack
    // tiles use the underlying server id so the bulk-action bar can
    // operate on the real row, matching the same Stack's behavior in
    // a collection/group view.
    useLongPressStackSelect(effectiveStackId, localRef);
    // Subscribe to derived booleans so the tile re-renders only when
    // its OWN selection state or the active mode changes — not on
    // every selection mutation elsewhere in the grid (which would
    // happen if we pulled the whole `stackSelection` Set as a snapshot).
    const isAnyMultiselect = app.useStore((s) => s.ui.isMultiselectMode);
    const isStackMultiselect = app.useStore((s) => s.ui.isMultiselectMode && s.stackSelection.size > 0);
    // Selection set is keyed by the *real* Stack id, so tiles rendered
    // via the synthetic-as-real-stack path read membership through
    // `effectiveStackId` — looking up `stack.id` (the synthetic FLIP
    // id) would always miss. Pure synthetic tiles have no
    // `effectiveStackId` and therefore can never be selected; the
    // `hasRealStackChrome` gate further down hides the overlay.
    const isThisSelected = app.useStore((s) => effectiveStackId !== undefined && s.stackSelection.has(effectiveStackId));
    // Single-shot "this tile is the freshly-created Stack the header's
    // create-empty-stack affordance just spawned — auto-open + auto-
    // enter inline rename" signal. We derive a boolean (rather than
    // returning the id and comparing) so Zustand's Object.is gate
    // suppresses re-renders on every OTHER tile when the signal fires
    // for an id none of them match. Routed through `effectiveStackId`
    // so synthetic-as-real-stack tiles activate when the underlying
    // real Stack id matches (mirrors the multiselect / rename /
    // delete paths). The matching tile's consumer effect below
    // clears the signal as part of acting on it.
    const isPendingNewStack = app.useStore((s) => effectiveStackId !== undefined && s.ui.pendingNewStackId === effectiveStackId);
    // Register the tile as a dnd-kit droppable so blocks dragged from
    // the surrounding grid (or from a different Stack's unfurl) can be
    // dropped onto the stack. The Stream's single `<DndContext>` owns
    // the drag pipeline — its `onDragEnd` reads
    // `over.data.current?.type === "stack"` and routes the drop
    // through `collectionStore.moveBlocksBetweenParentChild` (block →
    // stack, server enforces the parent-child relationship).
    //
    // `parentCollectionId` rides on the droppable's data so the routing
    // path doesn't need to look it up again — the move's source scope
    // for the optimistic write is the stack's parent (where the dragged
    // block lives in the grid).
    //
    // The droppable stays active even while the stack is open: the
    // tile's footprint inside the open unfurl is a legitimate "back
    // into this stack" target for cards being dragged from elsewhere.
    // Disabled only during inline rename + active multiselect: those
    // route the gesture through other affordances (input focus / tile
    // click) and a drop visual would compete for the foreground.
    // Drop target id wiring. Two synthetic flavors:
    //   - same-collection-per-day grouping: no server identity to
    //     drop into → droppable stays disabled.
    //   - user-created Stack-under-Primary (`synthetic.realStackId`
    //     is set): synthetic mechanism is just the rendering path;
    //     the underlying Stack is real. We expose its real id +
    //     parent so the drop handler can issue a proper
    //     parent → stack move POST.
    // Non-synthetic (real Stack tile) just uses its own
    // `stack.id` / `stack.parentCollectionId`.
    const dropStackId = synthetic?.realStackId ?? stack.id;
    const dropParentId = synthetic?.realStackParentId ?? stack.parentCollectionId;
    const hasServerIdentityForDrop = isSynthetic === false || synthetic?.realStackId !== undefined;
    const droppableData = useMemo(() => ({
        type: "stack",
        stackId: dropStackId,
        parentCollectionId: dropParentId,
    }), [dropStackId, dropParentId]);
    const { isOver, setNodeRef: setDroppableRef } = useDroppable({
        id: `stack-drop:${stack.id}`,
        data: droppableData,
        // Disable when there's no server identity to drop into (purely
        // client-side same-collection synthetic groupings) or when the
        // tile is in a competing interaction state (inline rename, any
        // multiselect overlay — both route the gesture through other
        // affordances and a drop visual would compete for the foreground).
        disabled: hasServerIdentityForDrop === false || isEditing || isAnyMultiselect,
    });
    // Track the in-flight drag's `active` so we can suppress the
    // drop-target visual when the drag SOURCE is one of this stack's
    // own unfurled blocks. dnd-kit fires `isOver` regardless of source,
    // but a stack-member from THIS stack landing back on its own tile
    // is a no-op (the Stream's `onDragEnd` filters `sourceStackId ===
    // targetStackId`), so the halo would be a lie.
    //
    // `useDndMonitor` keeps a local copy in sync rather than reading
    // from a shared context — the tile only needs to know "is the
    // source mine right now?", and the monitor avoids an extra
    // re-render of every droppable on the surface for every dragMove.
    //
    // Comparison routes through `dropStackId` (the REAL Stack id for
    // synthetic-as-real-stack tiles, `stack.id` for non-synthetic
    // ones) so the dragged member's `stackId` (also the real id)
    // matches the source tile on both sides. Using `stack.id` here
    // would compare the synthetic FLIP id against the real id and
    // always read as "foreign source", painting a misleading
    // drop-target halo on the source tile during a drag-out.
    const [activeDrag, setActiveDrag] = useState(undefined);
    useDndMonitor({
        onDragStart: (event) => setActiveDrag(event.active),
        onDragEnd: () => setActiveDrag(undefined),
        onDragCancel: () => setActiveDrag(undefined),
    });
    const isSameStackSource = activeDrag?.data.current?.type === "stack-member" &&
        activeDrag.data.current?.stackId === dropStackId;
    const showDropTarget = isOver && isSameStackSource === false;
    const mergedRef = useCallback((node) => {
        localRef.current = node;
        setDroppableRef(node);
        if (typeof ref === "function")
            ref(node);
        else if (ref)
            ref.current = node;
    }, [ref, setDroppableRef]);
    const handleClick = useCallback((e) => {
        e.stopPropagation();
        // Inline-rename is active — let the input handle its own
        // events, but DON'T toggle the unfurl out from under the user.
        // The input's `stopPropagation` already covers most of the
        // pill's footprint; this guard catches clicks on the pill's
        // empty padding that still bubble up to the card.
        if (isEditing) {
            e.preventDefault();
            return;
        }
        // Stack-multiselect → toggle THIS stack's membership in the
        // selection. Mirrors the block-multiselect / collection-
        // multiselect toggle paths. Synthetic-as-real-stack tiles
        // route through `effectiveStackId` so the selection set keeps
        // real Stack ids (matching long-press above and the bulk-
        // action bar's contract). Pure synthetic tiles with no
        // server identity short-circuit — the multiselect overlay
        // for them is hidden by `hasRealStackChrome` below.
        if (isStackMultiselect) {
            e.preventDefault();
            if (effectiveStackId === undefined)
                return;
            app.toggleStackSelection(effectiveStackId);
            return;
        }
        // Some OTHER multiselect mode is active (block / layer-1
        // collection). Stack-tile clicks are inert in that case —
        // don't open the unfurl, don't fold this stack into a foreign
        // selection set. The user has to exit the active mode first.
        if (isAnyMultiselect) {
            e.preventDefault();
            return;
        }
        if (!onOpen)
            return;
        if (isOpen !== true) {
            // Pure synthetic tile (no underlying real Stack id) fires
            // its own event. Stack-under-Primary synthetics — backed by
            // a real Stack via `synthetic.realStackId` — keep riding
            // through `block.stack_expanded` because the unfurl resolves
            // to actual server-side Stack contents.
            if (isSynthetic === true && synthetic?.realStackId === undefined) {
                track({
                    name: "block.synthetic_unfurled",
                    props: {
                        block_count: count,
                        parent_layer: "collection",
                    },
                });
            }
            else {
                track({
                    name: "block.stack_expanded",
                    props: {
                        stack_id_hash: hashId(stack.id),
                        block_count: count,
                        synthetic: isSynthetic,
                    },
                });
            }
        }
        onOpen(stack);
    }, [
        app,
        stack,
        onOpen,
        isStackMultiselect,
        isAnyMultiselect,
        isEditing,
        isOpen,
        count,
        isSynthetic,
        synthetic,
        effectiveStackId,
    ]);
    // Title-click navigation. Stops propagation so the tile's
    // open/close handler doesn't run for the same click (otherwise
    // tapping the title would BOTH navigate AND toggle the unfurl in
    // a single React commit, which is visibly jarring). We don't gate
    // on multiselect modes here because the title link itself is
    // hidden in those states — clicks pass through to the tile as a
    // plain `<span>` and the existing tile handler covers them.
    const handleNavigateToCollection = useCallback((e) => {
        e.stopPropagation();
        navigate(buildUrlForCollectionId(navigationCollectionId));
    }, [navigate, buildUrlForCollectionId, navigationCollectionId]);
    // Click handler for the explicit close-X affordance shown in the
    // open state. The whole tile is ALSO a close target via the outer
    // `handleClick` (clicking anywhere on the empty open tile closes
    // it), so this is the user-visible "close button" duplicating the
    // same behavior. Stop propagation so the tile's handler doesn't
    // fire twice (once here, once on bubble) — `onOpen` is a toggle,
    // so a double-fire would no-op back to the open state.
    const handleCloseClick = useCallback((e) => {
        e.stopPropagation();
        if (isOpen !== true)
            return;
        if (onOpen === undefined)
            return;
        onOpen(stack);
    }, [isOpen, onOpen, stack]);
    // Delete affordance — only rendered next to the pencil while the
    // stack is unfurled (the user has clearly engaged with this stack
    // and can see its contents; a delete button on a closed tile
    // would be too easy to mis-tap). Mirrors the bulk stack-delete
    // flow in the multiselect action bar: window.confirm gate, close
    // the unfurl first so the FLIP isn't animating into the tile we
    // just removed, then fire `softDelete` — the store's optimistic
    // cache eviction makes the tile disappear from the surrounding
    // grid in the same React commit. Soft-delete on a Stack does NOT
    // cascade into its blocks (only the Collection row is removed);
    // members stay alive and remain reachable from the homepage
    // stream and any other Collection they belong to, matching the
    // multiselect bar's contract.
    const handleDelete = useCallback((e) => {
        e.stopPropagation();
        if (isDeleting)
            return;
        // Guard: the trash button is only rendered when
        // `hasRealStackChrome` is true (so `effectiveStackId` is
        // always defined at this point), but the explicit check keeps
        // a stale ref through a re-render from issuing a soft-delete
        // against an undefined id.
        if (effectiveStackId === undefined)
            return;
        const displayTitle = stack.title || "Untitled stack";
        const ok = window.confirm(`Delete stack "${displayTitle}"? This cannot be undone from the app.`);
        if (ok === false)
            return;
        setIsDeleting(true);
        if (isOpen === true && onOpen !== undefined) {
            onOpen(stack);
        }
        showToast.success(`Deleted "${displayTitle}"`);
        // Route to the server-side Stack id. For non-synthetic this
        // equals `stack.id`; for synthetic-as-real-stack it's the
        // real Stack's id (the synthetic FLIP id has no server row).
        void collectionStore.softDelete(effectiveStackId).catch((err) => {
            setIsDeleting(false);
            showToast.error(err instanceof Error ? err.message : "Failed to delete stack");
        });
    }, [isDeleting, isOpen, onOpen, stack, effectiveStackId]);
    // Auto-focus + select-all on entering edit mode so the user can
    // immediately type a replacement name. Mirrors the modal's
    // desktop-rename flow (the modal skips this on mobile to avoid
    // popping the soft keyboard, but here the user EXPLICITLY clicked
    // the pencil so the soft keyboard is desired).
    //
    // `useLayoutEffect` so the focus + selection paint together with
    // the input mounting. For the "Create empty stack" flow (and
    // any future programmatic enter-edit path) this collapses what
    // would otherwise be "input visible, then a frame later it's
    // focused+selected" into a single paint where the highlighted
    // text is already there.
    useLayoutEffect(() => {
        if (isEditing === false)
            return;
        const input = inputRef.current;
        if (input === null)
            return;
        input.focus();
        input.select();
    }, [isEditing]);
    // Consume the `pendingNewStackId` signal published by the
    // collection header's "Create empty stack" affordance. When this
    // tile is the freshly-created Stack:
    //
    //   1. Kick off the unfurl via `onOpen(stack)` (only if we aren't
    //      already opening / open — guards against re-firing on a
    //      transient re-render). For an empty stack this lands the
    //      tile in the open state without a children FLIP.
    //   2. Flip local `isEditing` to true with the stored title pre-
    //      seeded — the auto-focus effect above then mounts the input
    //      focused + select-all'd in the same React commit.
    //   3. Clear the signal so subsequent re-renders don't re-fire.
    //
    // `useLayoutEffect` (not `useEffect`) so the open + edit state
    // applies SYNCHRONOUSLY after mount, BEFORE the browser paints
    // the closed-tile frame. Without this, the user would see a
    // single-frame flash (scatter + closed pill → open + input)
    // because `useEffect` queues its setStates until after paint,
    // forcing two paint cycles. With layout-effect they collapse
    // into one — the freshly-created tile lands directly on the
    // open+edit state, matching the "no flash" feel of the rest of
    // the optimistic create flow.
    //
    // Guarded on `isAnyMultiselect` because the rename input is
    // suppressed during multi-select (see the pencil + rename input
    // render gates further down). Firing the signal during multi-
    // select would set `isEditing` to a state the .tsx never renders,
    // leaving a stale flag the next render has to chase down. Better
    // to silently drop the auto-edit and let the multi-select bar
    // dismiss before the user names the stack — they can still click
    // the pencil afterward.
    useLayoutEffect(() => {
        if (isPendingNewStack === false)
            return;
        if (effectiveStackId === undefined)
            return;
        if (isAnyMultiselect === true)
            return;
        if (isOpen !== true && onOpen !== undefined) {
            onOpen(stack);
        }
        setDraftTitle(stack.title ?? "");
        setIsEditing(true);
        app.clearPendingNewStack();
    }, [isPendingNewStack, effectiveStackId, isAnyMultiselect, isOpen, onOpen, stack, app]);
    const handleStartEdit = useCallback((e) => {
        e.stopPropagation();
        // Seed with the STORED title — not the `"Untitled stack"`
        // fallback shown when `stack.title` is empty — so an unnamed
        // stack starts the rename with an empty input + placeholder.
        setDraftTitle(stack.title ?? "");
        setIsEditing(true);
    }, [stack.title]);
    const handleCancelEdit = useCallback(() => {
        setIsEditing(false);
        setDraftTitle("");
    }, []);
    const handleCommitEdit = useCallback(() => {
        if (isEditing === false)
            return;
        const trimmed = draftTitle.trim();
        const current = stack.title ?? "";
        // Always leave edit mode — empty / unchanged inputs are silent
        // no-ops (empty would fail `z.string().min(1)` anyway, and
        // toasting "no change" on a deliberate blur is just noise).
        setIsEditing(false);
        if (trimmed.length === 0 || trimmed === current)
            return;
        // The rename input is only mounted while `hasRealStackChrome`
        // is true, so this branch normally won't fire; the explicit
        // guard prevents a stale write against an undefined id if the
        // tile flips to pure-synthetic mid-edit.
        if (effectiveStackId === undefined)
            return;
        // Fire-and-forget. The store does an optimistic write into the
        // cross-layer cache + parent-children query caches BEFORE the
        // server round-trip, so by the time the next render commits the
        // new title is already visible in this tile's `stack.title`
        // prop. The catch path surfaces server errors as a toast — the
        // store has already rolled back the optimistic write. Routes to
        // `effectiveStackId` so synthetic-as-real-stack tiles rename
        // the underlying server row, not the synthetic FLIP id.
        void collectionStore.updateTitle(effectiveStackId, trimmed).catch((err) => {
            showToast.error(err instanceof Error ? err.message : "Failed to rename stack");
        });
    }, [draftTitle, effectiveStackId, stack.title, isEditing]);
    const handleEditKeyDown = useCallback((e) => {
        // The card's outer Enter/Space handler also opens the unfurl —
        // stop the keystrokes from bubbling so typing in the input
        // doesn't accidentally toggle the stack open.
        e.stopPropagation();
        if (e.key === "Enter") {
            e.preventDefault();
            handleCommitEdit();
        }
        else if (e.key === "Escape") {
            e.preventDefault();
            handleCancelEdit();
        }
    }, [handleCommitEdit, handleCancelEdit]);
    // The pill title is the stack's stored title verbatim (or the
    // "Untitled stack" fallback). We deliberately do NOT prefix
    // synthetic stacks with the day's M/D date here — the meta row
    // already carries "N blocks · 5h", and many collection titles
    // are themselves date-shaped (e.g. "5/26 · Polish & Bugs"), so
    // adding another prefix produced visibly doubled dates like
    // "5/21 · 5/26 · …" on the homepage.
    const title = stack.title || "Untitled stack";
    // Meta label format is keyed to what the tile REPRESENTS:
    //   - Grouping-activity synthetic (same-collection-per-day): the
    //     pill is a recap of "what got added to this collection
    //     today". The day-group header already establishes "when",
    //     so we drop the timestamp and lead with the verb:
    //         "added N blocks"
    //   - Real stack / user-created Stack-under-Primary synthetic:
    //     the pill represents an organizational bucket. Count + last-
    //     touched time is the canonical Stack meta (matches
    //     `<CollectionBlockView>`):
    //         "N blocks · 1h"
    // "blocks" matches the rest of the codebase's vocabulary —
    // `block` is the canonical unit name everywhere.
    const blockNoun = count === 1 ? "block" : "blocks";
    const isGroupingActivity = useSyntheticPillLayout;
    const countLabel = isGroupingActivity ? `added ${count} ${blockNoun}` : `${count} ${blockNoun}`;
    // Real-stack meta appends a relative-time suffix when the list
    // endpoint surfaced a `lastAddedAt`. Empty stacks fall back to
    // the bare count. `lastAddedAt` is also bumped optimistically by
    // `collectionStore` on `copyBlocks` / `addBlock`, so the suffix
    // stays fresh without a round-trip refresh. Grouping-activity
    // pills skip the suffix entirely (day-group header carries it).
    // Stack pills compress the sub-minute label from "just now" to
    // "now" so the meta line stays tight next to the count; other
    // surfaces (notifications, comments) keep the longer phrasing.
    const rawLastAddedLabel = isGroupingActivity === false && stack.lastAddedAt
        ? formatRelativeTime(stack.lastAddedAt)
        : "";
    const lastAddedLabel = rawLastAddedLabel === "just now" ? "now" : rawLastAddedLabel;
    const metaLabel = lastAddedLabel ? `${countLabel} · ${lastAddedLabel}` : countLabel;
    // Per-stack unique color — same palette entry the StackFrame
    // outline uses so the pill and container share a visual identity.
    // `notch` = saturated accent for the border; `bg` = very light
    // tint for the fill; `text` = accessible dark text on that bg.
    // For synthetic stacks `paletteId` is the REAL collection id so
    // every same-collection grouping reads as the same color across
    // days; for real stacks it falls back to the stack's own id.
    const stackColor = useMemo(() => getCollectionPaletteColor(paletteId), [paletteId]);
    // Dark-shell palette used when `darkBg` is on — same function
    // `<CollectionBlockView>` uses, so a synthetic Stack tile on the
    // home screen reads as the same surface as the parent Collection
    // card it groups under. Skipped entirely in the default light
    // mode so we never pay the contrast-search cost there.
    const darkCardColors = useMemo(() => (darkBg === true ? getCollectionCardColors(paletteId) : undefined), [darkBg, paletteId]);
    // Memoize so the scatter transforms stay stable across re-renders
    // — the hashes are deterministic but recomputing per render would
    // waste cycles in a dense grid. Synthetic stacks key off
    // `paletteId` for the same reason as the palette: same shape per
    // collection across days, instead of a different scatter every day.
    const placements = useMemo(() => placePeekTiles(paletteId, previewEnvelopes), [paletteId, previewEnvelopes]);
    const className = [
        "stack-block-view",
        darkBg === true ? "stack-block-view--dark" : "",
        isOpen ? "stack-block-view--open" : "",
        isThisSelected ? "stack-block-view--selected" : "",
        showDropTarget ? "stack-block-view--drop-target" : "",
    ]
        .filter(Boolean)
        .join(" ");
    // CSS custom properties for the dark banner — read by the
    // `.stack-block-view--dark .stack-block-view__pill` rules in
    // the .scss. Set on the ROOT element so they cascade down to the
    // pill (and the pill-count / pill-input children) without
    // recomputing per child. Skipped (left undefined) in the default
    // light mode so we don't pay for the extra style object.
    //
    // `--stack-open-text` is the dark-mode pill's text color WHEN
    // OPEN: the existing `--open` rule strips the pill's dark bg to
    // transparent so the underlying `<StackFrame>`'s light fill
    // shows through — at which point white closed-state text becomes
    // invisible. Reverting to `stackColor.text` (the same darkened
    // palette tint the light pill uses) gives readable text on the
    // light StackFrame surface without needing a second CSS variable
    // family for the open state. Cheap to set unconditionally inside
    // dark mode since the override only fires on `--dark.--open`.
    const rootStyle = darkCardColors
        ? {
            "--stack-dark-bg": darkCardColors.shellSurface,
            "--stack-dark-border": darkCardColors.border,
            "--stack-dark-title": darkCardColors.title,
            "--stack-dark-meta": darkCardColors.meta,
            "--stack-open-text": stackColor.text,
        }
        : undefined;
    const ariaLabel = isOpen ? `Close stack ${title}` : `Open stack ${title}`;
    const handleKeyDown = useCallback((e) => {
        if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleClick(e);
        }
    }, [handleClick]);
    return (_jsxs("div", { ref: mergedRef, role: "button", tabIndex: 0, className: className, style: rootStyle, onClick: handleClick, onKeyDown: handleKeyDown, "aria-label": ariaLabel, "aria-expanded": isOpen ? true : undefined, "data-stack-id": stack.id, children: [_jsx("div", { className: "stack-block-view__scatter", "aria-hidden": true, children: count === 0
                    ? null
                    : placements.length > 0
                        ? placements.map(({ envelope, jitterX, jitterY, jitterR, z }) => {
                            const tileStyle = {
                                "--peek-jitter-x": `${jitterX}px`,
                                "--peek-jitter-y": `${jitterY}px`,
                                "--peek-jitter-r": `${jitterR}deg`,
                                zIndex: z,
                            };
                            return (_jsx("div", { className: "stack-block-view__peek", style: tileStyle, children: _jsx(BlockMiniCapture, { envelope: envelope }) }, envelope.block.id));
                        })
                        : // Fallback while previews load: three blank slabs in a
                            // deterministic scatter so the card doesn't pop into a
                            // different shape when the previews resolve.
                            [0, 1, 2].map((index) => {
                                const jitterX = jitterFor(paletteId, index, 0) * MAX_TRANSLATE;
                                const jitterY = jitterFor(paletteId, index, 1) * MAX_TRANSLATE;
                                const jitterR = jitterFor(paletteId, index, 2) * MAX_ROTATE;
                                const z = 100 - index;
                                const tileStyle = {
                                    "--peek-jitter-x": `${jitterX}px`,
                                    "--peek-jitter-y": `${jitterY}px`,
                                    "--peek-jitter-r": `${jitterR}deg`,
                                    zIndex: z,
                                };
                                return (_jsx("div", { className: "stack-block-view__peek", style: tileStyle, children: _jsx("div", { className: "stack-block-view__peek-slab" }) }, `slab-${index}`));
                            }) }), _jsxs("div", { className: [
                    "stack-block-view__pill",
                    isSynthetic ? "stack-block-view__pill--synthetic" : "",
                    isGroupingActivity ? "stack-block-view__pill--grouping-activity" : "",
                ]
                    .filter(Boolean)
                    .join(" "), style: 
                // In dark mode the pill is borderless and transparent —
                // text colors come from the `--stack-dark-*` CSS vars set
                // on the root element. Skipping the inline style entirely
                // also keeps the open-state `transition: none` override on
                // the pill behaving identically across both modes (no
                // inline bg/border to chase down with `!important`).
                darkBg === true
                    ? undefined
                    : {
                        // Mirror the StackFrame's paint exactly:
                        //   border = notch (solid), background = notch blended
                        //   8% into white (solid, not transparent). The blend is
                        //   pixel-identical to "notch @ 8% over white" but emits
                        //   an opaque color so neither surface depends on the
                        //   page's actual background to read correctly.
                        backgroundColor: `color-mix(in srgb, ${stackColor.notch} 8%, white)`,
                        borderColor: stackColor.notch,
                        color: stackColor.text,
                    }, children: [_jsx("div", { className: "stack-block-view__pill-title-row", children: hasRealStackChrome && isEditing ? (
                        // Inline-rename active. Input takes the full title-row
                        // width (no pencil / delete are rendered alongside
                        // while editing). Enabled for every tile with a real
                        // server row backing it — real Stacks AND synthetic-
                        // as-real-stack — so the homepage Stack-under-Primary
                        // tile renames the same row a collection-view tile
                        // would.
                        _jsx("input", { ref: inputRef, type: "text", className: "stack-block-view__pill-input", value: draftTitle, maxLength: MAX_TITLE_LENGTH, placeholder: "Untitled stack", "aria-label": "Rename stack", onChange: (e) => setDraftTitle(e.target.value), onKeyDown: handleEditKeyDown, onBlur: handleCommitEdit, 
                            // Stop click + mousedown from bubbling to the card
                            // so the user can drag-select text / position the
                            // caret without toggling the unfurl. The card is
                            // also wired to long-press into stack-multiselect —
                            // the long-press hook reads mousedown, so this
                            // guard prevents text-selection drags from being
                            // misread as a long-press.
                            onClick: (e) => e.stopPropagation(), onMouseDown: (e) => e.stopPropagation(), onPointerDown: (e) => e.stopPropagation() })) : (_jsxs(_Fragment, { children: [isAnyMultiselect === false && isOpen === true ? (_jsxs("button", { type: "button", className: "stack-block-view__pill-title-link", "aria-label": `Open ${title}`, onClick: handleNavigateToCollection, 
                                    // Long-press → stack-multiselect reads mousedown
                                    // on the tile; stop it here so a click on the
                                    // title can't be misread as the start of a
                                    // selection drag.
                                    onMouseDown: (e) => e.stopPropagation(), children: [_jsx("span", { className: "stack-block-view__pill-title", children: title }), _jsx(FaIcon, { className: "stack-block-view__pill-title-arrow", icon: faArrowRight, "aria-hidden": true })] })) : (_jsx("span", { className: "stack-block-view__pill-title", children: title })), hasRealStackChrome && isAnyMultiselect === false ? (_jsx("button", { type: "button", className: "stack-block-view__rename-btn", "aria-label": `Rename ${title}`, onClick: handleStartEdit, onMouseDown: (e) => e.stopPropagation(), children: _jsx(FaIcon, { icon: faPencil }) })) : undefined, hasRealStackChrome && isAnyMultiselect === false ? (_jsx("button", { type: "button", className: "stack-block-view__delete-btn", "aria-label": `Delete ${title}`, onClick: handleDelete, onMouseDown: (e) => e.stopPropagation(), disabled: isDeleting, children: _jsx(FaIcon, { icon: isDeleting ? faCircleNotch : faTrashCan, spin: isDeleting }) })) : undefined] })) }), (isEditing && hasRealStackChrome) || isOpen === true ? undefined : (_jsxs("div", { className: "stack-block-view__pill-meta-row", children: [useSyntheticPillLayout ? (_jsx(SyntheticAddedByAvatars, { accountIds: synthetic.addedByAccountIds })) : null, _jsx("span", { className: [
                                    "stack-block-view__pill-count",
                                    isGroupingActivity ? "stack-block-view__pill-count--grouping-activity" : "",
                                ]
                                    .filter(Boolean)
                                    .join(" "), title: stack.lastAddedAt ?? undefined, children: metaLabel })] }))] }), _jsx(Tooltip, { label: "Close Stack", children: _jsx("button", { type: "button", className: "stack-block-view__close-circle", "aria-label": `Close stack ${title}`, tabIndex: isOpen ? 0 : -1, onClick: handleCloseClick, 
                    // Long-press → stack-multiselect on the tile reads mousedown;
                    // stop it here so a click on the X can't be misread as the
                    // start of a selection drag.
                    onMouseDown: (e) => e.stopPropagation(), style: { backgroundColor: stackColor.notch }, children: _jsx(FaIcon, { icon: faXmark, "aria-hidden": true }) }) }), isStackMultiselect && hasRealStackChrome ? (_jsx("div", { className: [
                    "stack-block-view__select-overlay",
                    isThisSelected ? "stack-block-view__select-overlay--checked" : "",
                ]
                    .filter(Boolean)
                    .join(" "), "aria-hidden": true, children: _jsx("div", { className: "stack-block-view__select-checkbox", children: isThisSelected ? _jsx(FaIcon, { icon: faCheck }) : null }) })) : null, canPin && !isOpen && !isAnyMultiselect ? (_jsx("div", { className: [
                    "stack-block-view__pin-anchor",
                    isPinned ? "stack-block-view__pin-anchor--pinned" : "",
                ]
                    .filter(Boolean)
                    .join(" "), onMouseDown: (e) => e.stopPropagation(), onClick: (e) => e.stopPropagation(), children: _jsx("button", { type: "button", className: [
                        "stack-block-view__pin-btn",
                        isPinned ? "stack-block-view__pin-btn--pinned" : "",
                    ]
                        .filter(Boolean)
                        .join(" "), "aria-label": isPinned ? `Unpin from ${parentTitle}` : `Pin to ${parentTitle}`, "aria-pressed": isPinned, onClick: handleTogglePin, children: _jsx(FaIcon, { icon: faThumbtack }) }) })) : undefined] }));
});
